import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import TypeAheadLookup from '../../../components/TypeAheadLookup';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import {
  ChevronDown, ChevronLeft, ChevronRight, X, Plus, Trash2,
  Layers, FileText, CheckSquare, Split, Paperclip,
  ScrollText, StickyNote, Package, Activity, Save,
  TrendingUp, BarChart2, Upload, Search, Wand2, Download,
  RefreshCw, Edit, FileSpreadsheet, ListOrdered, AlignJustify, Users
} from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../../../components/SortableTableHeader';

export default function UpdateAllModal({ open, onClose, req, segments, onSaved }) {
  const [rows, setRows] = useState([]);
  const [global, setGlobal] = useState({ item_due_date: '', account_code_4: '', account_code_5: '', account_code_6: '', supplier_code: '' });
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    if (!open) return;
    setRows((req.line_items || []).map(it => ({ ...it })));
    api.get('/api/data/suppliers', { params: { page_size: 200 } }).then(r => setSuppliers(r.data.data || [])).catch(() => {});
  }, [open, req]);

  const applyAll = () => {
    setRows(prev => prev.map(r => ({
      ...r,
      ...(global.item_due_date ? { item_due_date: global.item_due_date } : {}),
      ...(global.account_code_4 ? { account_code_4: global.account_code_4 } : {}),
      ...(global.account_code_5 ? { account_code_5: global.account_code_5 } : {}),
      ...(global.account_code_6 ? { account_code_6: global.account_code_6 } : {}),
      ...(global.supplier_code ? { supplier_code: global.supplier_code } : {}),
    })));
  };

  const updateRow = (id, field, val) => setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: val } : r));

  const save = async () => {
    try {
      await api.put(`/api/requisitions/${req.request_number}/mass-update`, {
        updates: rows.map(r => ({ item_id: r.id, item_due_date: r.item_due_date, account_code_4: r.account_code_4, account_code_5: r.account_code_5, account_code_6: r.account_code_6, supplier_code: r.supplier_code }))
      });
      toast.success('Mass update applied');
      onSaved(); onClose();
    } catch (err) { if (err?.code !== 'ERR_CANCELED' && err?.response?.status !== 401 && err?.response?.status !== 403) toast.error('Update failed'); }
  };

  const seg4 = segments.find(s => s.segment_number === 4);
  const seg5 = segments.find(s => s.segment_number === 5);
  const seg6 = segments.find(s => s.segment_number === 6);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Update All For Request — {req?.request_number}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground mb-3">Select required field(s) from the top row and click 'Apply All' to update all lines at once, or edit each row individually.</p>

        {/* Global apply row */}
        <div className="flex items-center gap-2 mb-4 p-3 bg-muted/20 rounded-xl">
          <div className="flex items-center gap-1"><Input type="date" value={global.item_due_date} onChange={e => setGlobal(p => ({...p, item_due_date: e.target.value}))} className="h-8 text-xs w-36" placeholder="Due Date" />{global.item_due_date && <button onClick={() => setGlobal(p => ({...p, item_due_date: ''}))} className="text-destructive"><X className="h-3 w-3" /></button>}</div>
          <Input value={global.account_code_4} onChange={e => setGlobal(p => ({...p, account_code_4: e.target.value}))} placeholder={seg4?.label || 'Expense Code 4'} className="h-8 text-xs w-28" />
          <Input value={global.account_code_5} onChange={e => setGlobal(p => ({...p, account_code_5: e.target.value}))} placeholder={seg5?.label || 'Expense Code 5'} className="h-8 text-xs w-28" />
          <Input value={global.account_code_6} onChange={e => setGlobal(p => ({...p, account_code_6: e.target.value}))} placeholder={seg6?.label || 'Expense Code 6'} className="h-8 text-xs w-28" />
          <select value={global.supplier_code} onChange={e => setGlobal(p => ({...p, supplier_code: e.target.value}))} className="h-8 rounded-lg border border-input bg-background px-2 text-xs flex-1">
            <option value="">-- Supplier --</option>
            {suppliers.map(s => <option key={s.supplier_code} value={s.supplier_code}>{s.supplier_code} — {s.supplier_name}</option>)}
          </select>
          <Button size="sm" onClick={applyAll} className="flex-shrink-0">Apply All</Button>
        </div>

        {/* Per-row edits */}
        <table className="w-full text-xs">
          <thead><tr className="bg-muted/50 border-b">
            <th className="text-left px-2 py-2">Item</th>
            <th className="text-left px-2 py-2">Item Code</th>
            <th className="text-left px-2 py-2">Item Description</th>
            <th className="text-left px-2 py-2">Due Date</th>
            <th className="text-left px-2 py-2">{seg4?.label || 'EC4'}</th>
            <th className="text-left px-2 py-2">{seg5?.label || 'EC5'}</th>
            <th className="text-left px-2 py-2">{seg6?.label || 'EC6'}</th>
            <th className="text-left px-2 py-2">Supplier</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b">
                <td className="px-2 py-1.5">{r.item_num}</td>
                <td className="px-2 py-1.5 font-mono text-xs">{r.item_code}</td>
                <td className="px-2 py-1.5 max-w-[150px] truncate">{r.item_description}</td>
                <td className="px-2 py-1.5"><div className="flex items-center gap-1"><Input type="date" value={r.item_due_date||''} onChange={e => updateRow(r.id,'item_due_date',e.target.value)} className="h-7 text-xs w-32" />{r.item_due_date && <button onClick={() => updateRow(r.id,'item_due_date','')} className="text-destructive"><X className="h-3 w-3" /></button>}</div></td>
                <td className="px-2 py-1.5"><Input value={r.account_code_4||''} onChange={e => updateRow(r.id,'account_code_4',e.target.value)} className="h-7 text-xs w-20" /></td>
                <td className="px-2 py-1.5"><Input value={r.account_code_5||''} onChange={e => updateRow(r.id,'account_code_5',e.target.value)} className="h-7 text-xs w-20" /></td>
                <td className="px-2 py-1.5"><Input value={r.account_code_6||''} onChange={e => updateRow(r.id,'account_code_6',e.target.value)} className="h-7 text-xs w-20" /></td>
                <td className="px-2 py-1.5">
                  <select value={r.supplier_code||''} onChange={e => updateRow(r.id,'supplier_code',e.target.value)} className="h-7 rounded border border-input bg-background px-1 text-xs w-36">
                    <option value="">--- Select ---</option>
                    {suppliers.map(s => <option key={s.supplier_code} value={s.supplier_code}>{s.supplier_code}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>X Cancel</Button>
          <Button onClick={save}><Save className="h-4 w-4 mr-1" /> Update All</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
